import { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { PageContainer } from '../components/PageContainer';
import { Button } from '../components/ui/Button';
import { EventForm } from '../components/EventForm';
import type { EventFormData } from '@wineryxplorer/shared';

export const EventCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const venueId = searchParams.get('venueId') || undefined;
  
  const [error, setError] = useState<string | null>(null);
  const formSubmitRef = useRef<(() => Promise<void>) | null>(null);

  // Fetch venue data if venueId is provided to pre-fill location
  const { data: venueData } = trpc.venue.getById.useQuery(
    { id: venueId || '' },
    { enabled: !!venueId }
  );

  const createEvent = trpc.event.create.useMutation({
    onSuccess: (data) => {
      // A venue admin who started here from the venue management hub is in the
      // middle of filling out that venue, not finished with it — send them back
      // to the hub so they can add the next event. Only an event created
      // outside that flow lands on its own public page.
      if (venueId) {
        navigate(`/wineries/${venueId}/manage`);
      } else {
        navigate(`/events/${data.id}`);
      }
    },
    onError: (error) => {
      setError(error.message);
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const handleSubmit = async (data: EventFormData) => {
    setError(null);
    await createEvent.mutateAsync(data);
  };

  const handleCreateClick = async () => {
    if (formSubmitRef.current) {
      await formSubmitRef.current();
    }
  };

  const handleCancel = () => {
    // Back to wherever the flow started: the venue's management hub, or the
    // public events list for an event created outside a venue.
    if (venueId) {
      navigate(`/wineries/${venueId}/manage`);
    } else {
      navigate('/events');
    }
  };

  // Prepare initial data with venue location if available
  const initialData: Partial<EventFormData> | undefined = venueData?.address
    ? {
        location: {
          name: venueData.name,
          address: {
            street: venueData.address.street,
            city: venueData.address.city,
            state: venueData.address.state,
            zipCode: venueData.address.zipCode,
            country: venueData.address.country,
          },
        },
      }
    : undefined;

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
      <PageContainer className="mt-2">
        <div className="border-b border-gray-200 pb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Create Event</h1>
          
          {/* Action Buttons - Top of Page */}
          <div className="flex gap-4 mb-6 justify-end">
            <Button
              variant="primary"
              onClick={handleCreateClick}
              disabled={createEvent.isPending}
              loading={createEvent.isPending}
            >
              {createEvent.isPending ? 'Creating...' : 'Create Event'}
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <svg
                className="w-5 h-5 text-red-400 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Error creating event
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Event Form */}
        <div className="mt-6">
          <EventForm
            initialData={initialData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            venueId={venueId}
            isSubmitting={createEvent.isPending}
            hideButtons
            submitRef={formSubmitRef}
          />
        </div>
      </PageContainer>
    </div>
  );
};
